const libFableService = require('fable-serviceproviderbase');
const libPictSectionModal = require('pict-section-modal');
const libHeadlightPopoverCSS = require('./Headlight-Popover-Service-CSS.js');

/**
 * HeadlightPopover
 *
 * Anchored, click-triggered popovers rendered from a Pict template hash. This
 * used to wrap Tippy.js; it now drives the pict-section-modal (PSM) dropdown in
 * `ContentHTML` mode, which gives click-outside / Escape dismissal, auto-flip,
 * viewport clamping and scroll/resize repositioning without a third-party dep.
 *
 * The public API (createPopover / hidePopover / showPopover / hideAll) is
 * preserved so existing call sites are untouched.
 */
class HeadlightPopoverFableService extends libFableService
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { ContentAssignment: import('pict/types/source/Pict-Content-Assignment') }}} */
		this.fable;
		this.serviceType = 'HeadlightPopover';

		// Legacy Tippy "theme" names mapped onto wrapper CSS classes that re-skin
		// the PSM dropdown surface (see Headlight-Popover-Service-CSS.js). Only
		// 'dropdown' and 'dropdown-dark' are used by consumers.
		this.themeClassMap = {
			'dropdown': 'headlight-popover',
			'dropdown-dark': 'headlight-popover headlight-popover-dark',
		};

		// Former Tippy default; used when a caller doesn't specify maxWidth.
		this.defaultMaxWidth = '350px';

		// Register the wrapper's CSS once, so the popover surface is themed even
		// in apps that never render the PSM view directly.
		if (this.fable && this.fable.CSSMap && typeof this.fable.CSSMap.addCSS === 'function')
		{
			this.fable.CSSMap.addCSS('Headlight-Popover-CSS', libHeadlightPopoverCSS, 500);
		}
	}

	/**
	 * @description Resolves the pict-section-modal view, registering it as a
	 *   singleton on first use if the host app hasn't already. Popover consumers
	 *   live in apps (e.g. headlight_panels) that don't register PSM themselves,
	 *   so the wrapper self-registers to stay a drop-in service.
	 * @returns {Object|null} The PSM view instance, or null if unavailable.
	 */
	_getModal()
	{
		if (!this.fable || !this.fable.views)
		{
			this.fable.log.error('HeadlightPopoverFableService: pict-section-modal is unavailable (no views registry)');
			return false;
		}
		let tmpModal = this.fable.views['Pict-Section-Modal'];
		if (!tmpModal && typeof this.fable.addViewSingleton === 'function')
		{
			this.fable.addViewSingleton('Pict-Section-Modal', libPictSectionModal.default_configuration, libPictSectionModal);
			tmpModal = this.fable.views['Pict-Section-Modal'];
		}
		if (!tmpModal)
		{
			this.fable.log.error('HeadlightPopoverFableService: could not resolve the Pict-Section-Modal view');
			return false;
		}
		return tmpModal;
	}

	/**
	 * @description Maps a legacy popover configuration onto PSM dropdown options.
	 * @param {Object} pConfiguration - The createPopover configuration.
	 * @returns {Object} Options for modal.dropdown().
	 */
	_mapDropdownOptions(pConfiguration)
	{
		const tmpTheme = pConfiguration.theme || 'dropdown';
		const tmpOptions =
		{
			className: this.themeClassMap[tmpTheme] || this.themeClassMap['dropdown'],
		};

		// maxWidth: Tippy accepted a number (px) or a CSS string.
		if (pConfiguration.maxWidth)
		{
			tmpOptions.maxWidth = (typeof pConfiguration.maxWidth === 'number')
				? pConfiguration.maxWidth + 'px'
				: pConfiguration.maxWidth;
		}
		else
		{
			tmpOptions.maxWidth = this.defaultMaxWidth;
		}

		// placement (Tippy) -> align / position (PSM), best-effort. PSM has no
		// true left/right side placement, so those fall back to auto-flip below/
		// above (documented gap). Only 'top*' forces above; everything else keeps
		// PSM's auto-flip. A trailing '-end' aligns to the anchor's right edge.
		const tmpPlacement = pConfiguration.placement || '';
		if (tmpPlacement.indexOf('top') === 0)
		{
			tmpOptions.position = 'above';
		}
		tmpOptions.align = (tmpPlacement.indexOf('-end') > -1) ? 'right' : 'left';
		if (pConfiguration.align)
		{
			tmpOptions.align = pConfiguration.align;
		}

		return tmpOptions;
	}

	/**
	 * @description Creates (and immediately opens) a popover from a Pict template.
	 * @param {Object} pConfiguration - Configuration for the popover
	 * @param {string} pConfiguration.PopoverTemplateHash - The template hash for the popover content
	 * @param {string} pConfiguration.PopoverDestinationAddress - The ID of the element to anchor the popover to
	 * @param {Object} pConfiguration.PopoverTemplateRecord - The record to use for the template
	 * @returns {boolean} - Returns true if the popover was created successfully, false otherwise
	 */
	createPopover(pConfiguration)
	{
		if (!pConfiguration)
		{
			this.fable.log.error('HeadlightPopoverFableService: createPopover: No configuration provided');
			return false;
		}
		if (!pConfiguration.PopoverTemplateHash)
		{
			this.fable.log.error('HeadlightPopoverFableService: createPopover: No template hash provided (should be a Pict-TemplateHash string)');
			return false;
		}
		if (!pConfiguration.PopoverDestinationAddress)
		{
			this.fable.log.error('HeadlightPopoverFableService: createPopover: No destination address provided (should be an ID string)');
			return false;
		}
		// if they passed over an element, warn them it should be an id
		if (typeof pConfiguration.PopoverDestinationAddress === 'object')
		{
			this.fable.log.error('HeadlightPopoverFableService: createPopover: PopoverDestinationAddress should be passed an ID, not an element object');
			return false;
		}
		// if they passed an id with a leading #, remove it
		if (pConfiguration.PopoverDestinationAddress.startsWith('#'))
		{
			pConfiguration.PopoverDestinationAddress = pConfiguration.PopoverDestinationAddress.substring(1);
			this.fable.log.warn('HeadlightPopoverFableService: createPopover: PopoverDestinationAddress should not have a leading #, removing it');
		}
		// find the anchor element by ID
		const tmpAnchorElement = document.getElementById(pConfiguration.PopoverDestinationAddress);
		if (!tmpAnchorElement)
		{
			this.fable.log.error('HeadlightPopoverFableService: createPopover: No anchor element found');
			return false;
		}

		const tmpModal = this._getModal();
		if (!tmpModal)
		{
			return false;
		}

		// Render the template to HTML and hand it to the dropdown verbatim.
		const tmpRecord = pConfiguration.PopoverTemplateRecord;
		// @ts-ignore - parseTemplateByHash is provided by the pict instance
		const tmpContentHTML = this.fable.parseTemplateByHash(pConfiguration.PopoverTemplateHash, tmpRecord);

		const tmpOptions = this._mapDropdownOptions(pConfiguration);
		tmpOptions.ContentHTML = tmpContentHTML;

		// Ensure the PSM + wrapper CSS is present (the modal view is never
		// rendered through the normal cycle, so inject explicitly).
		if (this.fable.CSSMap && typeof this.fable.CSSMap.injectCSS === 'function')
		{
			this.fable.CSSMap.injectCSS();
		}

		tmpModal.dropdown(tmpAnchorElement, tmpOptions);
		return true;
	}

	/**
	 * @description Manual "show" hook. Retained for API compatibility; with the
	 *   PSM dropdown a popover opens on createPopover(), so there is no separate
	 *   show step. No external callers use this.
	 * @returns {Boolean}
	 */
	showPopover()
	{
		this.fable.log.warn('HeadlightPopoverFableService: showPopover is a no-op; popovers open on createPopover()');
		return false;
	}

	/**
	 * @description Hides the open popover (PSM keeps a single dropdown open at a
	 *   time, so this dismisses whichever popover is showing). Arguments are
	 *   accepted for API compatibility but ignored.
	 * @returns {Boolean}
	 */
	hidePopover()
	{
		const tmpModal = this.fable.views && this.fable.views['Pict-Section-Modal'];
		if (!tmpModal)
		{
			return false;
		}
		tmpModal.dismissDropdowns();
		return true;
	}

	/**
	 * @description Hides all popovers created by this service.
	 * @returns {Boolean}
	 */
	hideAll()
	{
		const tmpModal = this.fable.views && this.fable.views['Pict-Section-Modal'];
		if (!tmpModal)
		{
			return false;
		}
		tmpModal.dismissDropdowns();
		return true;
	}
}

module.exports = HeadlightPopoverFableService;
module.exports.default_configuration = {};
